<template>
  <div class="article-detail-page">
    <inner-hero
      background-image="/images/article/article-hero.webp"
      :title="heroTitle"
      :show-breadcrumb="false"
    >
      <h1 class="article-detail-hero-title">{{ heroTitle }}</h1>
      <div class="article-detail-hero-nav" role="group" aria-label="文章操作">
        <div class="article-detail-hero-context">
          <nuxt-link
            :to="listPath"
            class="article-detail-hero-action article-detail-hero-back"
            :aria-label="`返回${categoryLabel}列表`"
            :title="`返回${categoryLabel}列表`"
          >
            <i class="i-lucide-arrow-left" aria-hidden="true"></i>
          </nuxt-link>
          <span class="article-detail-hero-category">{{ categoryLabel }}</span>
        </div>
        <button
          type="button"
          class="article-detail-hero-action article-detail-hero-share"
          aria-label="分享链接"
          title="分享链接"
          @click="shareCurrentArticle"
        >
          <i class="i-lucide-share-2" aria-hidden="true"></i>
        </button>
      </div>
    </inner-hero>

    <section class="section article-detail-section">
      <div class="page-container article-detail-container">
        <div v-if="pending" class="article-detail-state" role="status">
          <i class="i-lucide-loader-circle is-spinning" aria-hidden="true"></i>
          <span>正在加载内容...</span>
        </div>

        <div v-else-if="!article" class="article-detail-state is-error" role="alert">
          <i class="i-lucide-file-x" aria-hidden="true"></i>
          <strong>内容不存在或已下线</strong>
          <p>该内容可能已撤回、尚未发布，或当前地址不属于本栏目。</p>
          <nuxt-link :to="listPath">返回{{ categoryLabel }}</nuxt-link>
        </div>

        <article v-else class="article-detail-shell">
          <div class="article-detail-context">
            <span class="article-detail-context-label">{{ categoryLabel }}</span>
            <nav class="article-detail-breadcrumb" aria-label="面包屑">
              <nuxt-link to="/">首页</nuxt-link>
              <span aria-hidden="true">/</span>
              <nuxt-link v-if="parentLabel" :to="listPath">{{ parentLabel }}</nuxt-link>
              <span v-if="parentLabel" aria-hidden="true">/</span>
              <span v-if="parentLabel" aria-current="page">{{ categoryLabel }}</span>
              <nuxt-link v-else :to="listPath">{{ categoryLabel }}</nuxt-link>
            </nav>
          </div>

          <header class="article-detail-heading">
            <h1>{{ article.title }}</h1>
            <!-- <p v-if="article.subtitle" class="article-detail-subtitle">{{ article.subtitle }}</p> -->
            <div class="article-detail-heading-footer">
              <div class="article-detail-meta">
                <span v-if="article.author">作者：{{ article.author }}</span>
                <span v-if="article.source">来源：{{ article.source }}</span>
                <span>
                  发布时间：
                  <time :datetime="article.publish_date">
                    {{ formatSiteDate(article.publish_date) }}
                  </time>
                </span>
              </div>
              <button
                type="button"
                class="article-detail-copy"
                aria-label="分享链接"
                title="分享链接"
                @click="shareCurrentArticle"
              >
                <i class="i-lucide-share-2" aria-hidden="true"></i>
              </button>
            </div>
          </header>

          <div v-if="article.media_type === 2 && article.video_url" class="article-detail-video">
            <video
              v-if="!videoFailed"
              :key="article.id"
              :src="article.video_url"
              :poster="article.cover_url || undefined"
              :aria-label="article.title"
              controls
              playsinline
              preload="none"
              @error="videoFailed = true"
            ></video>
            <div v-else class="article-detail-state is-error" role="alert">
              <strong>视频暂时无法播放</strong>
              <button type="button" class="article-video-retry" @click="videoFailed = false">
                重新加载
              </button>
            </div>
          </div>

          <div class="article-detail-reader">
            <!-- 正文仅来自已认证后台 CMS，保持其结构化富文本。 -->
            <div
              v-if="article.content"
              class="article-detail-content w-e-text-container"
              v-html="article.content"
            ></div>
            <div v-else class="article-detail-empty">正文内容正在整理中。</div>
          </div>
        </article>
      </div>
    </section>

    <contact-action />
  </div>
</template>

<script setup lang="ts">
  import type { ApiData, ArticleDetailItem } from '~/types/site-api'

  import { findArticleCategory, formatSiteDate, getArticleCategoryRoute } from '~/utils/content'
  import {
    createArticleJsonLd,
    createBreadcrumbJsonLd,
    createSiteUrl,
    getSiteUrl,
    siteSeo,
  } from '~/utils/seo'

  defineOptions({
    name: 'ArticleDetailPage',
  })

  const route = useRoute()
  const { getArticle } = useSiteApi()
  const { success, error: showErrorToast } = useToast()
  const routeCategoryCode = computed(() => {
    const value = route.params.category
    return String(Array.isArray(value) ? (value[0] ?? '') : (value ?? ''))
  })
  const { data: categoryResponse } = useArticleCategories()
  const categoryMatch = computed(() =>
    findArticleCategory(categoryResponse.value?.data ?? [], routeCategoryCode.value)
  )
  const category = computed(() => categoryMatch.value?.category)
  const rootCategory = computed(() => categoryMatch.value?.root)
  const categoryRoute = computed(() =>
    category.value ? getArticleCategoryRoute(category.value) : undefined
  )
  const listPath = computed(() => categoryRoute.value?.path ?? '/articles')
  const parentLabel = computed(() =>
    rootCategory.value && rootCategory.value !== category.value
      ? rootCategory.value.name
      : undefined
  )
  const articleId = computed(() => String(route.params.id ?? ''))
  const videoFailed = shallowRef(false)
  watch(articleId, () => {
    videoFailed.value = false
  })
  const detailPath = computed(() => `${listPath.value}/${encodeURIComponent(articleId.value)}`)
  const siteUrl = getSiteUrl()

  if (!categoryRoute.value) {
    throw createError({
      statusCode: 404,
      statusMessage: '资讯栏目不存在',
    })
  }

  const {
    data: articleResponse,
    pending,
    error,
  } = await useAsyncData<ApiData<ArticleDetailItem>>(
    `article-detail-${routeCategoryCode.value}-${articleId.value}`,
    () => getArticle(articleId.value),
    { watch: [articleId] }
  )

  const article = computed(() => {
    const item = articleResponse.value?.data
    if (!item || error.value || item.category.code !== routeCategoryCode.value) {
      return undefined
    }
    return item
  })
  const categoryLabel = computed(() => categoryRoute.value?.label ?? '资讯')
  const heroTitle = computed(() => rootCategory.value?.name ?? categoryLabel.value)
  const seoTitle = computed(() =>
    article.value
      ? `${article.value.title} | ${siteSeo.shortName}`
      : `${categoryLabel.value} | ${siteSeo.shortName}`
  )
  const seoDescription = computed(
    () => article.value?.summary || article.value?.subtitle || categoryLabel.value
  )

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: 'article',
    ogImage: computed(() => article.value?.cover_url || siteSeo.ogImage),
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
    twitterImage: computed(() => article.value?.cover_url || siteSeo.ogImage),
  })

  useHead(() => ({
    link: [
      {
        rel: 'canonical',
        href: createSiteUrl(detailPath.value, siteUrl),
      },
    ],
  }))
  useJsonLd(
    () =>
      article.value
        ? createArticleJsonLd(
            {
              article: article.value,
              category: categoryLabel.value,
              url: createSiteUrl(detailPath.value, siteUrl),
            },
            siteUrl
          )
        : undefined,
    () =>
      createBreadcrumbJsonLd(
        [
          { name: '首页', path: '/' },
          { name: categoryLabel.value, path: listPath.value },
          { name: article.value?.title || '文章详情', path: detailPath.value },
        ],
        siteUrl
      )
  )

  /** 移动端唤起系统分享，桌面端复制当前详情地址。 */
  async function shareCurrentArticle() {
    if (!import.meta.client) return

    const url = window.location.href
    const isTouchDevice = window.matchMedia('(hover: none) and (pointer: coarse)').matches

    if (isTouchDevice && navigator.share) {
      try {
        await navigator.share({
          title: article.value?.title ?? document.title,
          text: article.value?.summary || article.value?.subtitle || undefined,
          url,
        })
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return
        showErrorToast({ title: '分享失败', description: '暂时无法调起系统分享，请稍后重试。' })
      }
      return
    }

    try {
      await navigator.clipboard.writeText(url)
      success({ description: '复制链接成功，快去给他人分享吧。' })
    } catch {
      showErrorToast({ description: '复制失败，请手动复制浏览器地址。' })
    }
  }
</script>

<style scoped lang="scss">
  @keyframes article-detail-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .article-detail-section {
    position: relative;
    z-index: 2;
    padding: 24px 0 clamp(52px, 6vw, 80px);
    margin-top: 0;
    background: transparent;
    isolation: isolate;
  }

  .article-detail-section::before {
    position: absolute;
    inset: 0;
    z-index: -1;
    content: '';
    background: var(--surface-soft);
  }

  .article-detail-container {
    position: relative;
  }

  :deep(.inner-hero h1) {
    font-size: clamp(28px, 3vw, 40px);
  }

  /* 移除文章详情页 hero 的渐变遮罩 */
  :deep(.inner-hero)::after {
    content: none;
  }

  .article-detail-hero-nav {
    display: none;
    gap: 24px;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .article-detail-hero-context {
    display: flex;
    gap: 16px;
    align-items: center;
    min-width: 0;
  }

  .article-detail-hero-action {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    color: rgb(255 255 255 / 80%);
    cursor: pointer;
    background: transparent;
    border: 0;
    transition: color 0.2s ease;
  }

  .article-detail-hero-action i {
    width: 20px;
    height: 20px;
  }

  .article-detail-hero-share i {
    width: 18px;
    height: 18px;
  }

  .article-detail-hero-action:focus-visible {
    color: var(--white);
    outline: 2px solid var(--white);
    outline-offset: 2px;
  }

  .article-detail-hero-category {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: clamp(20px, 2vw, 28px);
    font-weight: 600;
    line-height: 1.35;
    color: var(--white);
    white-space: nowrap;
  }

  .article-detail-context {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    gap: 12px 18px;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 12px;
    margin-bottom: 30px;
    border-bottom: 2px solid var(--line);
  }

  .article-detail-context::after {
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 120px;
    height: 2px;
    content: '';
    background: var(--accent);
  }

  .article-detail-context-label {
    position: relative;
    padding-left: 12px;
    font-size: 18px;
    font-weight: 600;
    color: var(--ink);
  }

  .article-detail-context-label::before {
    position: absolute;
    top: 50%;
    left: 0;
    width: 4px;
    height: 19px;
    content: '';
    background: var(--accent);
    transform: translateY(-50%);
  }

  .article-detail-breadcrumb {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    font-size: 14px;
    color: var(--more);
  }

  .article-detail-breadcrumb a {
    color: inherit;
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .article-detail-breadcrumb [aria-current] {
    font-weight: 600;
    color: var(--accent);
  }

  .article-detail-shell {
    position: relative;
    padding: clamp(28px, 3vw, 44px);
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: var(--radius-md);
    box-shadow: 0 18px 50px rgb(45 31 32 / 8%);
  }

  .article-detail-heading {
    padding-bottom: 30px;
    border-bottom: 1px solid var(--line);
  }

  .article-detail-heading h1 {
    max-width: 24em;
    margin: 0;
    font-size: clamp(30px, 3.2vw, 46px);
    font-weight: 620;
    line-height: 1.22;
  }

  .article-detail-subtitle {
    max-width: 72ch;
    margin: 18px 0 0;
    font-size: 16px;
    line-height: 1.75;
    color: var(--muted);
  }

  .article-detail-heading-footer {
    display: flex;
    flex-wrap: wrap;
    gap: 16px 24px;
    align-items: center;
    justify-content: space-between;
    margin-top: 24px;
  }

  .article-detail-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 18px;
    align-items: center;
    font-size: 14px;
    color: var(--muted);
  }

  .article-detail-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    font-size: 16px;
    color: var(--ink);
    cursor: pointer;
    background: transparent;
    border: 0;
    transition: color 0.2s ease;
  }

  .article-detail-copy i {
    width: 18px;
    height: 18px;
  }

  .article-detail-copy:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .article-detail-cover {
    aspect-ratio: 16 / 10;
    margin-top: clamp(32px, 4vw, 52px);
    overflow: hidden;
    background: #eef0f2;
    border-radius: var(--radius-md);
  }

  .article-detail-video {
    margin-top: 32px;
  }

  .article-detail-video video {
    display: block;
    width: 100%;
    max-height: 65dvh;
    aspect-ratio: 16 / 9;
    object-fit: contain;
    background: #0a0a0b;
    border-radius: 8px;
  }

  .article-video-retry {
    padding: 8px 16px;
    color: var(--accent);
    cursor: pointer;
    background: var(--surface);
    border: 1px solid var(--line);
    border-radius: 4px;
  }

  .article-video-retry:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }

  .article-detail-reader {
    width: 100%;
    margin-top: clamp(32px, 4vw, 52px);
  }

  .article-detail-empty {
    padding: 40px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface-soft);
    border: 1px dashed var(--line);
    border-radius: var(--radius-md);
  }

  .article-detail-state {
    display: grid;
    gap: 12px;
    place-content: center;
    justify-items: center;
    min-height: 360px;
    padding: 40px 24px;
    color: var(--muted);
    text-align: center;
    background: var(--surface);
    border: 1px dashed var(--line);
    border-radius: var(--radius-md);
  }

  .article-detail-state > i {
    width: 38px;
    height: 38px;
    color: var(--accent);
  }

  .article-detail-state strong {
    font-size: 20px;
    color: var(--ink);
  }

  .article-detail-state p {
    margin: 0;
  }

  .article-detail-state a {
    min-height: 44px;
    padding: 10px 18px;
    margin-top: 8px;
    font-weight: 600;
    color: var(--white);
    background: var(--accent);
    border-radius: var(--radius-md);
  }

  .is-spinning {
    animation: article-detail-spin 900ms linear infinite;
  }

  @media (min-width: 1440px) {
    .article-detail-container {
      max-width: 1080px;
      padding-inline: 0;
    }

    :deep(.inner-hero-content) {
      max-width: 1080px;
      padding-inline: 0;
    }
  }

  @media (min-width: 768px) {
    .article-detail-shell {
      margin-top: -120px;
    }
  }

  @media (max-width: 767px) {
    .article-detail-section {
      padding: 0 0 44px;
      margin-top: 0;
    }

    .article-detail-section::before {
      inset: 0;
    }

    :deep(.inner-hero) {
      min-height: 0;
    }

    :deep(.inner-hero-content) {
      padding-block: 36px;
      transform: translateY(-18px);
    }

    .article-detail-hero-title,
    .article-detail-context,
    .article-detail-copy {
      display: none;
    }

    .article-detail-container {
      display: flow-root;
      padding-inline: 12px;
    }

    .article-detail-shell {
      padding: 20px 18px 30px;
      margin-top: -36px;
    }

    .article-detail-heading {
      padding-bottom: 24px;
    }

    .article-detail-hero-nav {
      display: grid;
      grid-template-columns: 32px minmax(0, 1fr) 32px;
      gap: 12px;
    }

    .article-detail-hero-context {
      display: contents;
    }

    .article-detail-hero-action {
      width: 32px;
      height: 32px;
    }

    .article-detail-hero-action i {
      width: 21px;
      height: 21px;
    }

    .article-detail-hero-share i {
      width: 19px;
      height: 19px;
    }

    .article-detail-hero-category {
      font-size: 17px;
      text-align: center;
    }

    .article-detail-heading h1 {
      font-size: 22px;
      line-height: 1.34;
    }

    .article-detail-heading-footer {
      margin-top: 18px;
    }

    .article-detail-meta {
      gap: 8px 14px;
      width: 100%;
      font-size: 13px;
      line-height: 1.6;
    }

    .article-detail-reader {
      margin-top: 30px;
    }

    .article-detail-content {
      font-size: 16px;
      line-height: 1.82;
    }

    .article-detail-content :deep(h1) {
      font-size: 25px;
    }

    .article-detail-content :deep(h2) {
      font-size: 23px;
    }

    .article-detail-content :deep(h3) {
      font-size: 20px;
    }

    .article-detail-content :deep(blockquote) {
      padding: 18px;
    }

    .article-detail-content :deep(table) {
      display: block;
      overflow-x: auto;
      white-space: nowrap;
    }
  }

  @media (hover: hover) and (pointer: fine) {
    .article-detail-breadcrumb a:hover,
    .article-detail-copy:hover {
      color: var(--accent);
    }

    .article-detail-hero-action:hover {
      color: var(--white);
    }
  }

  @media (hover: none) {
    .article-detail-breadcrumb a:active,
    .article-detail-copy:active {
      color: var(--accent);
    }

    .article-detail-hero-action:active {
      color: var(--white);
    }
  }

  @media print {
    .article-detail-page :deep(.contact-action),
    .article-detail-page :deep(.inner-hero),
    .article-detail-copy,
    .article-detail-context,
    .article-detail-hero-nav {
      display: none !important;
    }

    .article-detail-section {
      padding: 0;
      margin-top: 0;
      background: var(--white);
    }

    .article-detail-section::before {
      display: none;
    }

    .article-detail-shell {
      padding: 0;
      border: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .is-spinning {
      animation: none;
    }
  }
</style>
